import SectionName from '~/constants/new-cars/SectionName';

// Uses hasEverLoaded (not isLoaded) to prevent the section from flickering
// back during data refetches when the user selects options within a section.
export default function deriveActiveSection(
  sectionSequence: SectionName[],
  hasSelectedModelVersion: boolean,
  hasSelectedDoors: boolean,
  hasSelectedYear: boolean,
  trimsIsLoaded: boolean,
  trimsCount: number,
  hasSelectedTrim: boolean,
  hasSelectedTruckBodyStyle: boolean,
  customizeOptionsIsLoaded: boolean,
  optionsCount: number,
  modelVersionsIsLoaded: boolean = true
): SectionName {
  // After a year is selected, that year's modelVersions are refetched. Until
  // they load, sectionSequence omits ModelVersion/Doors and trims still holds
  // the previous year's data, so the rules below would advance to a phantom
  // Trim/Options step — which the animation then plays as a backward move,
  // sliding the next section in from the top. Hold at Year until modelVersions
  // resolves and the real next step is known.
  if (
    sectionSequence.includes(SectionName.Year) &&
    hasSelectedYear &&
    !modelVersionsIsLoaded
  ) {
    return SectionName.Year;
  }

  const yearResolved =
    !sectionSequence.includes(SectionName.Year) || hasSelectedYear;

  const priorSectionsSkipped =
    yearResolved &&
    !sectionSequence.includes(SectionName.ModelVersion) &&
    !sectionSequence.includes(SectionName.Doors);

  const truckBodyStyleResolved =
    !sectionSequence.includes(SectionName.TruckBodyStyle) ||
    hasSelectedTruckBodyStyle;

  if (
    priorSectionsSkipped &&
    trimsIsLoaded &&
    trimsCount === 0 &&
    truckBodyStyleResolved
  ) {
    return SectionName.Options;
  }

  if (
    hasSelectedTrim &&
    truckBodyStyleResolved &&
    customizeOptionsIsLoaded &&
    optionsCount > 0
  ) {
    return SectionName.Options;
  }

  if (hasSelectedTrim && sectionSequence.includes(SectionName.TruckBodyStyle)) {
    if (!hasSelectedTruckBodyStyle) return SectionName.TruckBodyStyle;
    // Hold at truckBodyStyle while customize is loading. We check both
    // hasEverLoaded (false on first load) and optionsCount (0 after a
    // customizeOptions.reset() call, which zeroes data without resetting
    // hasEverLoaded) so that a reset-then-reselect cycle doesn't fall through
    // to 'trim' while waiting for the new fetch to complete.
    if (!customizeOptionsIsLoaded || optionsCount === 0)
      return SectionName.TruckBodyStyle;
  }

  const modelVersionResolved =
    !sectionSequence.includes(SectionName.ModelVersion) ||
    hasSelectedModelVersion;
  const doorsResolved =
    !sectionSequence.includes(SectionName.Doors) || hasSelectedDoors;

  if (
    yearResolved &&
    modelVersionResolved &&
    doorsResolved &&
    trimsIsLoaded &&
    trimsCount > 0
  ) {
    return SectionName.Trim;
  }

  if (
    yearResolved &&
    modelVersionResolved &&
    sectionSequence.includes(SectionName.Doors) &&
    !hasSelectedDoors
  ) {
    return SectionName.Doors;
  }

  if (
    yearResolved &&
    sectionSequence.includes(SectionName.ModelVersion) &&
    !hasSelectedModelVersion
  ) {
    return SectionName.ModelVersion;
  }

  // All pre-trim steps are resolved but trims haven't loaded yet. Return the
  // deepest pre-trim section so we don't fall back past Year.
  if (yearResolved && modelVersionResolved && doorsResolved) {
    if (sectionSequence.includes(SectionName.Doors)) return SectionName.Doors;
    if (sectionSequence.includes(SectionName.ModelVersion))
      return SectionName.ModelVersion;
  }

  return sectionSequence[0];
}
